import { ComponentProps, forwardRef } from "react";
import { Typography } from "@/components/atoms";
import { BookingFlightStatus } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";

export interface BookingFlightStatusCodeProps extends ComponentProps<typeof Typography> {
  status: BookingFlightStatus;
  delay?: number;
  type?: "default" | "vibrant";
}

const flightColorsDefault: Record<BookingFlightStatus, string> = {
  not_found: "bg-neutral-dark-gray/20 text-neutral-dark-gray",
  unknown: "bg-neutral-dark-gray/20 text-neutral-dark-gray",
  scheduled: "bg-success-light text-success",
  "en-route": "bg-success-light text-success",
  "on-time": "bg-success-light text-success",
  cancelled: "bg-danger-light text-danger",
  delayed: "bg-warning-light text-warning-dark",
  early: "bg-info-light text-info",
  diverted: "bg-warning/10 text-warning",
  incident: "bg-warning/10 text-warning",
  landed: "bg-success-light text-success",
};

const flightColorsVibrant: Record<BookingFlightStatus, string> = {
  not_found: "bg-neutral-dark-gray",
  unknown: "bg-neutral-dark-gray",
  scheduled: "bg-success",
  "en-route": "bg-success",
  "on-time": "bg-success",
  cancelled: "bg-danger",
  delayed: "bg-warning",
  early: "bg-info",
  diverted: "bg-warning",
  incident: "bg-warning",
  landed: "bg-success",
};

/**
 * What a status is CALLED, exported so a second badge cannot invent its own vocabulary for the same
 * eleven codes — the client booking sheet renders one (`FlightStatusBadge`) in the operator app's colours
 * but its own shape, and "Delayed" here against "Late" there is the same flight described two ways.
 */
export const flightStatusLabels: Record<BookingFlightStatus, string> = {
  not_found: "Not Found",
  unknown: "Pending",
  scheduled: "On-Time",
  "en-route": "En-Route",
  "on-time": "On-Time",
  cancelled: "Cancelled",
  delayed: "Delayed",
  early: "Early",
  diverted: "Diverted",
  incident: "Incident",
  landed: "Landed",
};

/**
 * The status a badge actually shows: `delayed` / `early` / `on-time` are not codes the backend sends, they
 * are read off the DELAY of a flight still in the air. Exported for the same reason as the labels — the
 * synthesis is the rule that turns `scheduled` + `-12` into "Early", and a second badge deriving it again
 * is a second rule to keep in step.
 *
 * A ZERO delay is only a performance claim for a flight that is already flying. Before departure the feed
 * has said nothing about the flight, so a not-yet-departed flight keeps its own `scheduled` code and
 * `en-route` at zero — genuinely airborne and genuinely to time — keeps `on-time`. That separation is what
 * lets the CLIENT badge withhold itself entirely for a flight nothing is known about. The operator surface
 * labels both "On-Time".
 *
 * A non-zero delay still resolves either way: `scheduled` + 12 is `delayed`, `scheduled` − 12 is `early`. A
 * flight the feed has moved is being reported on, whether or not it has left the ground.
 */
export const resolveFlightStatusCode = (status: BookingFlightStatus, delay?: number): BookingFlightStatus => {
  const inTheAir = status === "scheduled" || status === "en-route";
  if (typeof delay !== "number" || !inTheAir) return status;
  if (delay > 0) return "delayed";
  if (delay < 0) return "early";
  return status === "en-route" ? "on-time" : "scheduled";
};

export const BookingFlightStatusCode = forwardRef<HTMLHeadingElement & HTMLSpanElement, BookingFlightStatusCodeProps>((props, ref) => {
  const { status, delay, type = "default", className, ...rest } = props;
  const statusWithDelay = resolveFlightStatusCode(status, delay);

  return (
    <Typography
      variant="small"
      className={clsx(
        "flex rounded-xl px-4 py-0.5 capitalize leading-snug text-white",
        type === "vibrant" ? flightColorsVibrant[statusWithDelay] : flightColorsDefault[statusWithDelay],
        className
      )}
      ref={ref}
      {...rest}
    >
      {flightStatusLabels[statusWithDelay]}
    </Typography>
  );
});

BookingFlightStatusCode.displayName = "BookingFlightStatusCode";
